import type { ReactNode } from 'react';
import { DAY_NAMES, bucketLabel, formatSeconds, hourLabel } from '../../lib/kyckConnect/insightFormat';
import { countAxis, formatMss, percentUp, qualityTotals, secondsAxis, type Axis } from '../../lib/kyckConnect/chartAxis';
import type { CallQualityPoint, SeriesPoint } from '../../types/kyckConnectInsights';

// THE PORTAL'S CHARTS (Dashboard and Reporting polish). Same data, same honesty rules as the plain charts in ReportView (a null speed is a
// GAP, never a zero), drawn so they can be READ: a y axis with three or four labelled values and gridlines, ONE axis label size, and a
// plot that is a fixed 240px tall whatever the card's width.
//
// ⚠️ THE LABELS ARE HTML, NOT SVG TEXT. SVG text lives in the viewBox's units, so a chart scaled to a wide card would draw its "11px" labels at
// 18px. Here the plot is a stretched SVG (preserveAspectRatio none, strokes `non-scaling`) and every label is an ordinary element at a true
// 11px, positioned by percentage. The points of the speed line are zero-length round-capped strokes for the same reason: a circle would
// stretch into an ellipse.
// ⚠️ Staff keep the plain charts: ReportView only uses these when `polished` is set (the portal), so the staff look does not change.

export const PLOT_HEIGHT = 240;

/** Thin the x labels so they never collide: about eight, always the first. */
export function xTickIndexes(n: number): Set<number> {
  const step = Math.max(1, Math.ceil(n / 8));
  const out = new Set<number>();
  for (let i = 0; i < n; i += step) out.add(i);
  return out;
}

function Plot({ axis, fmt, keys, label, testId, children }: { axis: Axis; fmt: (v: number) => string; keys: readonly string[]; label: string; testId: string; children: ReactNode }) {
  const slot = 100 / Math.max(1, keys.length);
  const ticks = xTickIndexes(keys.length);
  return (
    <div className="kc-plot" role="img" aria-label={label} data-testid={testId} style={{ ['--kc-plot-h' as string]: `${PLOT_HEIGHT}px` }}>
      <div className="kc-plot-y" aria-hidden="true">
        {axis.ticks.map((t) => <span key={t} className="kc-axis" style={{ bottom: `${percentUp(t, axis)}%` }}>{fmt(t)}</span>)}
      </div>
      <div className="kc-plot-area">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="kc-plot-svg" aria-hidden="true" focusable="false">
          {axis.ticks.map((t) => <line key={t} x1="0" x2="100" y1={100 - percentUp(t, axis)} y2={100 - percentUp(t, axis)} className="kc-grid" vectorEffect="non-scaling-stroke" data-grid={t} />)}
          {children}
        </svg>
      </div>
      <div className="kc-plot-x" aria-hidden="true">
        {keys.map((k, i) => (ticks.has(i) ? <span key={k} className="kc-axis" style={{ left: `${slot * i + slot / 2}%` }}>{bucketLabel(k)}</span> : null))}
      </div>
    </div>
  );
}

const countFmt = (v: number): string => v.toLocaleString('en-AU');

/** Bars per bucket, the previous period (when asked for) as a line over the same slots, matched by position. */
export function PolishedBarChart({ points, previous, label }: { points: readonly SeriesPoint[]; previous?: readonly SeriesPoint[] | null; label: string }) {
  const keys = points.map((p) => p.key);
  const prevValues = (previous ?? []).map((p) => p.value ?? 0);
  const axis = countAxis(Math.max(0, ...points.map((p) => p.value ?? 0), ...prevValues));
  const slot = 100 / Math.max(1, points.length);
  return (
    <Plot axis={axis} fmt={countFmt} keys={keys} label={label} testId="bar-chart">
      {points.map((p, i) => {
        const v = p.value ?? 0;
        const h = Math.max(v === 0 ? 0.6 : 1.2, percentUp(v, axis));
        return <rect key={p.key} x={slot * i + slot * 0.12} y={100 - h} width={slot * 0.76} height={h} className="fill-primary" opacity={v === 0 ? 0.25 : 1}><title>{`${bucketLabel(p.key)}: ${v}`}</title></rect>;
      })}
      {previous && previous.length > 0 && (
        <polyline fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke" className="stroke-muted-foreground" data-testid="previous-line" points={prevValues.slice(0, points.length).map((v, i) => `${slot * i + slot / 2},${100 - percentUp(v, axis)}`).join(' ')} />
      )}
    </Plot>
  );
}

/** A line of medians in m:ss. A null bucket BREAKS the line (no call dialled in it): it is never drawn as zero. */
export function PolishedGapLine({ points, label }: { points: readonly SeriesPoint[]; label: string }) {
  const values = points.map((p) => p.value);
  const axis = secondsAxis(Math.max(0, ...values.map((v) => v ?? 0)));
  const slot = 100 / Math.max(1, points.length);
  const x = (i: number): number => slot * i + slot / 2;
  const y = (v: number): number => 100 - percentUp(v, axis);
  const segments: Array<Array<[number, number]>> = [];
  let cur: Array<[number, number]> = [];
  values.forEach((v, i) => {
    if (v === null) {
      if (cur.length) segments.push(cur);
      cur = [];
    } else cur.push([x(i), y(v)]);
  });
  if (cur.length) segments.push(cur);
  return (
    <Plot axis={axis} fmt={formatMss} keys={points.map((p) => p.key)} label={label} testId="gap-line-chart">
      {segments.filter((s) => s.length > 1).map((s, i) => <polyline key={i} fill="none" strokeWidth="2.5" strokeLinejoin="round" vectorEffect="non-scaling-stroke" className="stroke-primary" points={s.map(([a, b]) => `${a},${b}`).join(' ')} />)}
      {values.map((v, i) => (v === null ? null : (
        <line key={points[i]!.key} x1={x(i)} x2={x(i)} y1={y(v)} y2={y(v)} strokeWidth="7" strokeLinecap="round" vectorEffect="non-scaling-stroke" className="stroke-primary" data-point={v}>
          <title>{`${bucketLabel(points[i]!.key)}: ${formatSeconds(v)}`}</title>
        </line>
      )))}
    </Plot>
  );
}

const QUALITY = [
  { id: 'booked', label: 'Booked', fill: 'fill-primary', opacity: 1, swatch: 'kc-swatch-booked' },
  { id: 'goodConversation', label: 'Good conversation', fill: 'fill-primary', opacity: 0.45, swatch: 'kc-swatch-good' },
  { id: 'notAFit', label: 'Not a fit', fill: 'fill-muted-foreground', opacity: 0.35, swatch: 'kc-swatch-fit' },
] as const;

/** Stacked bars of how connected calls ended, with a legend that carries each category's total (colour is never the only signal). */
export function PolishedQualityChart({ points }: { points: readonly CallQualityPoint[] }) {
  const axis = countAxis(Math.max(0, ...points.map((p) => p.booked + p.goodConversation + p.notAFit)));
  const slot = 100 / Math.max(1, points.length);
  const totals = qualityTotals(points);
  return (
    <div data-testid="quality-block">
      <Plot axis={axis} fmt={countFmt} keys={points.map((p) => p.key)} label="Call quality by period" testId="quality-chart">
        {points.map((p, i) => {
          let y = 100;
          return (
            <g key={p.key}>
              <title>{`${bucketLabel(p.key)}: ${p.booked} booked, ${p.goodConversation} good conversation, ${p.notAFit} not a fit`}</title>
              {QUALITY.map((q) => {
                const h = percentUp(p[q.id], axis);
                y -= h;
                return h > 0 ? <rect key={q.id} x={slot * i + slot * 0.12} y={y} width={slot * 0.76} height={h} className={q.fill} opacity={q.opacity} /> : null;
              })}
            </g>
          );
        })}
      </Plot>
      <ul className="kc-legend" data-testid="quality-legend">
        {QUALITY.map((q) => (
          <li key={q.id}>
            <span className={`kc-swatch ${q.swatch}`} aria-hidden="true" />
            <span>{q.label}</span>
            <span className="kc-legend-total" data-total={totals[q.id]}>{countFmt(totals[q.id])}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Horizontal bars. A long name wraps at a word boundary (two lines at most before the row grows); it is never cut mid-word. */
export function PolishedRowBars({ rows }: { rows: Array<{ label: string; count: number; extra?: string }> }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (rows.length === 0) return <p className="text-muted-foreground" data-testid="chart-empty">Nothing in this period yet.</p>;
  return (
    <ul className="space-y-2" data-testid="row-bars">
      {rows.map((r) => (
        <li key={r.label} className="flex items-center gap-3">
          <span className="kc-rowlabel w-44 shrink-0 text-foreground">{r.label}</span>
          <span className="h-3 rounded-sm bg-primary" style={{ width: `${Math.max(2, (r.count / max) * 100)}%` }} />
          <span className="tabular-nums text-muted-foreground">{r.count}{r.extra ? ` ${r.extra}` : ''}</span>
        </li>
      ))}
    </ul>
  );
}

/** The arrival heatmap, filling its card: a fixed-layout table, the day column fixed and the 24 hour columns sharing the rest. */
export function PolishedHeatmap({ cells }: { cells: ReadonlyArray<{ dow: number; hour: number; count: number }> }) {
  const max = Math.max(1, ...cells.map((c) => c.count));
  const at = (dow: number, hour: number): number => cells.find((c) => c.dow === dow && c.hour === hour)?.count ?? 0;
  const order = [1, 2, 3, 4, 5, 6, 0];
  return (
    <div data-testid="heatmap">
      <table className="kc-heat w-full table-fixed border-separate border-spacing-0.5">
        <colgroup>
          <col style={{ width: '2.75rem' }} />
          {Array.from({ length: 24 }, (_, h) => <col key={h} />)}
        </colgroup>
        <thead>
          <tr>
            <th />
            {Array.from({ length: 24 }, (_, h) => <th key={h} className="kc-axis font-normal text-muted-foreground">{h % 3 === 0 ? hourLabel(h) : ''}</th>)}
          </tr>
        </thead>
        <tbody>
          {order.map((d) => (
            <tr key={d}>
              <th className="kc-axis pr-1 text-right font-normal text-muted-foreground">{DAY_NAMES[d]}</th>
              {Array.from({ length: 24 }, (_, h) => {
                const n = at(d, h);
                return <td key={h} title={`${DAY_NAMES[d]} ${hourLabel(h)}: ${n}`} className="kc-heat-cell h-6 rounded-sm bg-primary" style={{ opacity: n === 0 ? 0.08 : 0.2 + 0.8 * (n / max) }} />;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
